import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { SavedProductList, UserPrompt } from 'components';
import { useAuth, useAuthB2C } from 'hooks';
import { useUser } from 'selectors';
import { User } from 'types';
import { useTranslation } from 'react-i18next';
import theme, { themeColors } from 'themes';
import LogoutIcon from '../../icons/img/Logout.svg';
import DropdownSelect from 'components/DropdownMenu/DropdownSelect';
import ChevronUp from 'icons/indexed-icons/ChevronUp';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import SettingIcon from 'icons/indexed-icons/SettingsPlus';
import { useHistory } from 'react-router-dom';

const MainContainer = styled.div`
  height: 40px;
  width: 72px;
  position: relative;
  display: flex;
`;
const MenuHeaderSetting = styled.div`
  display: flex;
  margin-top: 24px;
  cursor: pointer;
`;
const OuterBadgeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  .icon path {
    stroke-width: 2;
  }
`;
const MenuFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const InnerBadgeContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: #e5e9ed;
  color: #475467;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
`;

interface PersonBadgeProps {
  name: string;
}

const MenuItem = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.313rem 0.625rem;
  height: 36px;
  > div {
    cursor: pointer;
  }

  &:hover {
    background-color: ${themeColors.lightGrey2};
  }
`;
const MenuItemIcon = styled.div`
  margin-right: 0.063rem;
  display: flex;
`;
const SMenuItemIcon = styled.div`
  margin-right: 12px;
  display: flex;
`;
const MenuItemText = styled.div`
  color: ${theme.colors.primaryBlue5};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
const MenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  border-bottom: 0.063rem solid ${themeColors.lightGrey4};
  padding-bottom: 0.625rem;
`;
const MenuHeaderNameId = styled.div`
  font-weight: 700;
`;
const MenuHeaderEmailId = styled.div`
  color: ${themeColors.lightGrey8};
`;
const PopupContainer = styled.ul`
  position: absolute;
  top: 125%;
  right: 0.2rem;
  background-color: white;
  min-width: 13.375rem;
  padding: 0.625rem;
  border: 0.063rem solid ${themeColors.mediumBlue2};
  list-style: none;
  margin: 0px;
  border-radius: 0 0 0.625rem 0.625rem;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.1);
}
`;

const MenuItemsMobile = styled.div`
  @media (min-width: 541px) {
    display: none;
  }
  border-bottom: 0.063rem solid #dedede;
  margin: 0.625rem;
  padding-bottom: 1.125rem;
`;

const ActiveIcon = styled.div`
  padding: 1px 4px 2px;
  background: ${theme.colors.white};
  border-radius: 3px;
`;

const TextBadge: React.FC<PersonBadgeProps> = ({ name }) => {
  const auth = useAuth();
  const authB2C = useAuthB2C();
  const user = useUser() as User;
  const history = useHistory();
  const { t } = useTranslation(['common']);
  const b2cflag: boolean = import.meta.env.VITE_ENABLE_B2C == 'true';
  const [isLogoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  const handleCancelLogout = () => {
    setLogoutModalOpen(false);
  };

  const handleLogout = () => {
    b2cflag ? authB2C.signout() : auth.signout();
  };

  const getInitials = (name: string): string => {
    const names = name.split(' ');
    const initials = names.map((n) => n.charAt(0));
    return initials.join('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option: string) => {
    switch (option) {
      case 'logout':
        setLogoutModalOpen(!isLogoutModalOpen);
        break;
      case 'settings':
        setIsOpen(!isOpen);
        history.push(`/user/${user.id}/settings/basic-information`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <MainContainer>
      <OuterBadgeContainer ref={dropdownRef}>
        <InnerBadgeContainer>{getInitials(name)}</InnerBadgeContainer>
        <div>
          <ActiveIcon
            onClick={handleToggle}
            style={{
              cursor: 'pointer',
              backgroundColor: isOpen ? theme.colors.white : 'transparent'
            }}
          >
            {isOpen ? (
              <ChevronUp width="14px" />
            ) : (
              <ChevronDown width="14px" color={theme.colors.lightGrey10} />
            )}
          </ActiveIcon>
          {isOpen && (
            <PopupContainer>
              <MenuHeader>
                <MenuHeaderNameId>
                  {user?.firstName || ''} {user?.lastName || ''}
                </MenuHeaderNameId>
                <MenuHeaderEmailId>{user?.email || ''}</MenuHeaderEmailId>
                <MenuHeaderSetting>
                  <SMenuItemIcon>
                    <SettingIcon />
                  </SMenuItemIcon>
                  <MenuItemText
                    onClick={() => {
                      handleOptionSelect('settings');
                    }}
                  >
                    {' '}
                    Settings
                  </MenuItemText>
                </MenuHeaderSetting>
              </MenuHeader>
              <MenuItemsMobile>
                <MenuItem>
                  <SavedProductList />
                </MenuItem>
                <MenuItem>
                  <DropdownSelect />
                </MenuItem>
              </MenuItemsMobile>
              <MenuFooter>
                <MenuItem>
                  <MenuItemIcon>
                    <img src={LogoutIcon} />
                  </MenuItemIcon>
                  <MenuItemText
                    onClick={() => {
                      handleOptionSelect('logout');
                    }}
                  >
                    {' '}
                    {t('log_out')}
                  </MenuItemText>
                </MenuItem>
              </MenuFooter>
            </PopupContainer>
          )}
        </div>
      </OuterBadgeContainer>
      {isLogoutModalOpen && (
        <UserPrompt
          visible={isLogoutModalOpen}
          message={t('logout_prompt')}
          subMessage={t('you_will_be_logged_out_in') as string}
          primaryActionLabel={t('continue_session')}
          secondaryActionLabel={t('log_out')}
          handleCancel={handleCancelLogout}
          handlePrimaryAction={handleCancelLogout}
          handleSecondaryAction={handleLogout}
          timeoutAction="secondary-action"
        />
      )}
    </MainContainer>
  );
};

export default TextBadge;
